/**
 * t301u: the assistant on the phone. The sparkle in the top bar opens it full screen over the current page (which stays
 * mounted, so its route and scroll are untouched); Back closes it. A link followed from an answer closes it and leaves
 * its history entry behind, so coming Back reopens the assistant where you left it. While the keyboard is up the screen
 * is pinned to the visible area, so the composer sits on the keyboard and the answers scroll above it.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { useBackClosesDrawer } from '@/lib/origin';
import { useUi } from '@/lib/store';
import { Spinner } from '@/components/ui';
import { ActionChat } from '@/features/assistant/ActionChat';
import { AssistantConversation, ModeTabs, useAssistantModes, type Mode } from '@/features/assistant/Assistant';
import { keyboardBox, useKeyboardInset } from './keyboard';

export const ASSISTANT_DRAWER = 'assistant';

/** Shell side: Back closes the assistant, leaving the page closes it, and coming Back to its entry reopens it. */
export function usePhoneAssistant(): boolean {
  const open = useUi(s => s.assistantOpen);
  const close = useUi(s => s.closeAssistant);
  const reopen = useUi(s => s.openAssistant);
  useBackClosesDrawer(open, close, ASSISTANT_DRAWER);
  const loc = useLocation();
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    const here = (window.history.state as { drawer?: unknown } | null)?.drawer === ASSISTANT_DRAWER;
    const { assistantOpen } = useUi.getState();
    if (here && !assistantOpen) reopen();
    else if (!here && assistantOpen) close();
  }, [loc.key]); // eslint-disable-line react-hooks/exhaustive-deps -- on navigation only
  return open;
}

export function PhoneAssistant() {
  const close = useUi(s => s.closeAssistant);
  const modes = useAssistantModes();
  const [mode, setMode] = useState<Mode>('act');
  const m: Mode = modes.act && (mode === 'act' || !modes.search) ? 'act' : 'search';
  const kb = useKeyboardInset();
  return (
    <div className={clsx('fixed inset-0 z-40 flex flex-col bg-white dark:bg-slate-950', !kb.inset && 'pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]')}
      style={keyboardBox(kb)} role="dialog" aria-modal="true" aria-label="Assistant" data-testid="m-assistant">
      <div className="flex h-12 shrink-0 items-center gap-1 border-b border-slate-200 pr-3 dark:border-slate-800">
        <button type="button" onClick={close} aria-label="Back" className="flex h-12 w-11 items-center justify-center text-slate-700 dark:text-slate-200" data-testid="m-assistant-back"><ChevronLeft className="h-6 w-6" /></button>
        <Sparkles className="h-4 w-4 text-slate-500" aria-hidden />
        <h1 className="text-base font-semibold">Assistant</h1>
      </div>
      {modes.act && modes.search && <ModeTabs mode={m} setMode={setMode} />}
      {modes.loading ? <div className="p-4"><Spinner /></div> : m === 'act' ? <ActionChat /> : <AssistantConversation origin="panel" />}
    </div>
  );
}
